import cx from 'classnames';
import { RadialProgress } from 'components/admin/ui/Chart/RadialProgress';
import { Anchor } from 'components/ui/Anchor';
import { CheckCircle, MinusCircle, UploadCloud01 } from 'ethena-icons';
import _ from 'lodash';
import { ChangeEvent, useRef } from 'react';
import { FormV2 } from '../../FormV2';
import { FileIcon } from '../FileIcon';
import css from './MultiFileUpload.module.scss';

export interface IFileState {
  error?: string;
  file: File;
  fileId?: number;
  status: 'idle' | 'loading' | 'success' | 'error';
  uploadPercentage: number;
  uploadStartTime?: number;
  url?: string;
}

interface IMultiFileUploadProps {
  accept: string;
  buttonClassName?: string;
  className?: string;
  errorMessage?: string;
  fileStatesByName: Record<string, IFileState>;
  handleRemoveFile: (file: File) => void;
  isDisabled?: boolean;
  isInvalid?: boolean;
  label?: string;
  onChange: (files: File[]) => void;
}

export const MultiFileUpload = ({
  accept,
  buttonClassName,
  className,
  errorMessage,
  fileStatesByName,
  handleRemoveFile,
  isDisabled,
  isInvalid,
  label = 'Select files...',
  onChange,
}: IMultiFileUploadProps) => {
  const inputRef = useRef<HTMLInputElement>();

  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = _.toArray(ev.target.files);
    onChange(selectedFiles);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const renderUploadStateIcon = (state: IFileState) => {
    const { status, uploadPercentage } = state;

    if (status === 'success') {
      return (
        <CheckCircle
          className={cx(css.successIcon, css.iconPop)}
          height={16}
          width={16}
        />
      );
    }

    if (status === 'loading') {
      return (
        <div className={css.progressWrapper}>
          <RadialProgress
            percentage={uploadPercentage}
            showPercentage={false}
            size={16}
            strokeWidth={2}
          />
        </div>
      );
    }

    if (!isDisabled || status === 'error' || status === 'idle') {
      return (
        <MinusCircle
          className={cx(css.remove, css.iconFadeIn)}
          height={16}
          width={16}
          onClick={() => handleRemoveFile(state.file)}
        />
      );
    }

    return null;
  };

  const renderFiles = () => {
    return _.map(fileStatesByName, (state) => (
      <div
        className={cx(css.fileContainer, {
          [css.invalid]: state.status === 'error',
        })}
        key={state.file.name}
      >
        <div className={css.fileRow}>
          <div className={css.pseudoInput}>
            {state.url ? (
              <Anchor href={state.url} target="_blank">
                <FileIcon className="mr-2" fileName={state.file.name} />
                {state.file.name}
              </Anchor>
            ) : (
              <p>
                <FileIcon className="mr-2" fileName={state.file.name} />
                {state.file.name}
              </p>
            )}
          </div>
          {renderUploadStateIcon(state)}
        </div>
        <FormV2.Control.Feedback type="invalid">
          {state.error || 'Upload failed, please try again'}
        </FormV2.Control.Feedback>
      </div>
    ));
  };

  return (
    <div className={cx(css.container, className)}>
      <div
        className={cx(css.buttonContainer, {
          [css.invalid]: isInvalid,
        })}
      >
        <button
          className={cx(css.pseudoInput, buttonClassName)}
          onClick={() => inputRef.current?.click?.()}
          type="button"
        >
          {label}
          <UploadCloud01 height={16} width={16} />
        </button>
        <FormV2.Control.Feedback type="invalid">
          {errorMessage}
        </FormV2.Control.Feedback>
      </div>
      <input
        accept={accept}
        hidden
        multiple
        onChange={handleChange}
        ref={inputRef}
        type="file"
      />
      {renderFiles()}
    </div>
  );
};
